/**
 * CopyField Component
 * 
 * A reusable copy-to-clipboard component with consistent UX across TitleFlow.
 * 
 * Features:
 * - One-click copy functionality
 * - Visual feedback (tooltip + optional toast)
 * - Keyboard accessible (tab + enter)
 * - Works for all roles without permission restrictions
 * - Multiple display variants
 * 
 * Usage:
 *   <CopyField value="TF-20260224-0001" label="Order Number" />
 *   <CopyField value={order.id} />
 *   <CopyField value={email} showToast />
 *   <CopyField value={address} variant="inline" />
 */

import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { cn } from '../../lib/utils';

/**
 * CopyField Component
 * 
 * @param {string} value - The value to copy to clipboard
 * @param {string} label - Optional label for the field (used in toast message)
 * @param {string} displayValue - Optional different value to display (defaults to value)
 * @param {boolean} showToast - Show toast notification on copy (default: true)
 * @param {boolean} showLabel - Show label before the value (default: false)
 * @param {string} variant - Display variant: 'default' | 'inline' | 'compact' | 'badge'
 * @param {string} className - Additional CSS classes
 * @param {boolean} truncate - Truncate long values (default: false)
 * @param {number} maxLength - Max characters before truncation (default: 50)
 * @param {string} size - Icon size: 'sm' | 'md' | 'lg' (default: 'sm')
 */
export const CopyField = ({
  value,
  label,
  displayValue,
  showToast = true,
  showLabel = false,
  variant = 'default',
  className = '',
  truncate = false,
  maxLength = 50,
  size = 'sm',
  disabled = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Icon sizes
  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Handle copy action
  const handleCopy = useCallback(async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (disabled || !value) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      
      if (showToast) {
        toast.success(label ? `${label} copied!` : 'Copied to clipboard!', {
          duration: 2000,
        });
      }
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  }, [value, label, showToast, disabled]);

  // Handle keyboard interaction
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCopy(e);
    }
  }, [handleCopy]);

  // Get display text
  const getDisplayText = () => {
    const text = displayValue || value || '';
    if (truncate && text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  };

  // Don't render if no value
  if (!value && value !== 0) {
    return null;
  }

  // Variant styles
  const variantStyles = {
    default: 'inline-flex items-center gap-1.5 group',
    inline: 'inline-flex items-center gap-1 text-inherit',
    compact: 'inline-flex items-center gap-0.5',
    badge: 'inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md text-sm font-medium',
  };

  // Button styles based on variant
  const buttonStyles = {
    default: `p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors ${
      copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'
    }`,
    inline: `p-0.5 rounded hover:bg-gray-100/50 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
      copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'
    }`,
    compact: `p-0.5 rounded hover:bg-gray-100/50 focus:outline-none transition-colors ${
      copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'
    }`,
    badge: `p-0.5 rounded hover:bg-gray-200 focus:outline-none transition-colors ${
      copied ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'
    }`,
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div 
        className={cn(variantStyles[variant], className)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Label (optional) */}
        {showLabel && label && (
          <span className="text-gray-500 text-sm mr-1">{label}:</span>
        )}
        
        {/* Value */}
        <span 
          className={cn(
            'select-all',
            truncate && 'truncate max-w-xs',
            variant === 'badge' && 'text-gray-800'
          )}
          title={truncate && String(value).length > maxLength ? String(value) : undefined}
        >
          {getDisplayText()}
        </span>
        
        {/* Copy Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className={cn(
                buttonStyles[variant],
                disabled && 'opacity-50 cursor-not-allowed',
                variant === 'default' && !isHovered && 'opacity-0 group-hover:opacity-100'
              )}
              aria-label={`Copy ${label || 'value'} to clipboard`}
              tabIndex={0}
              data-testid={`copy-btn-${label?.toLowerCase().replace(/\s+/g, '-') || 'field'}`}
            >
              {copied ? (
                <Check className={cn(iconSizes[size], 'text-green-600')} />
              ) : (
                <Copy className={iconSizes[size]} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {copied ? 'Copied!' : `Copy ${label || 'to clipboard'}`}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

/**
 * CopyIconButton - Standalone copy button without displaying value
 * Use when you only need the copy icon next to existing content
 * 
 * @param {string} value - The value to copy
 * @param {string} label - Label for accessibility and toast
 * @param {string} size - Icon size
 * @param {string} className - Additional classes
 */
export const CopyIconButton = ({
  value,
  label,
  size = 'sm',
  className = '',
  showToast = true,
  disabled = false,
}) => {
  const [copied, setCopied] = useState(false);

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const handleCopy = useCallback(async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (disabled || !value) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      
      if (showToast) {
        toast.success(label ? `${label} copied!` : 'Copied!', { duration: 2000 });
      }
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy');
    }
  }, [value, label, showToast, disabled]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCopy(e);
    }
  }, [handleCopy]);

  if (!value && value !== 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={cn(
              'p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors',
              copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            aria-label={`Copy ${label || 'value'}`}
            tabIndex={0}
            data-testid={`copy-icon-${label?.toLowerCase().replace(/\s+/g, '-') || 'btn'}`}
          >
            {copied ? (
              <Check className={iconSizes[size]} />
            ) : (
              <Copy className={iconSizes[size]} />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {copied ? 'Copied!' : `Copy ${label || ''}`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * useCopyToClipboard - Custom hook for copy functionality
 * Use when you need more control over the copy behavior
 */
export const useCopyToClipboard = () => {
  const [copiedValue, setCopiedValue] = useState(null);

  const copy = useCallback(async (value, label, options = {}) => {
    const { showToast = true } = options;
    
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedValue(value);
      
      if (showToast) {
        toast.success(label ? `${label} copied!` : 'Copied to clipboard!', {
          duration: 2000,
        });
      }
      
      setTimeout(() => setCopiedValue(null), 2000);
      return true;
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
      return false;
    }
  }, []);

  const isCopied = useCallback((value) => {
    return copiedValue === value;
  }, [copiedValue]);

  return { copy, isCopied, copiedValue };
};

export default CopyField;
